/**
 * <surisa-contacto-upload> — adjuntos del formulario de contacto.
 *
 * Sube cada archivo al Cloudflare Worker `surisa-contacto` (action=upload),
 * que lo mete en Shopify Files y devuelve { fileId, url }. Al enviar el
 * formulario, primero registra la solicitud como metaobject (action=submit,
 * panel en Admin → Contenido → Metaobjetos) y después deja seguir el submit
 * nativo de Shopify ({% form 'contact' %}) para que el email a surisa@ llegue
 * como siempre, con los enlaces de los adjuntos en el campo contact[Adjuntos].
 *
 * Si el worker falla o no responde, el formulario nativo se envía igualmente
 * (el email nunca se pierde; solo se pierde la entrada del panel).
 *
 * Config vía data-attributes (poblados desde schema settings en Liquid):
 *   data-endpoint   URL del worker (sin query string)
 *   data-max-mb     tamaño máx. por archivo (MB)
 *   data-max-files  nº máx. de archivos
 *   data-exts       extensiones permitidas, csv
 *   data-label-uploading / data-label-error / data-label-remove
 *
 * Setup completo: docs/ARCHITECTURE_contacto_uploads.md
 */
(function () {
  'use strict';

  if (customElements.get('surisa-contacto-upload')) return;

  class SurisaContactoUpload extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
      if (!this.endpoint) return;

      this.maxBytes = (parseInt(this.dataset.maxMb, 10) || 25) * 1024 * 1024;
      this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 8;
      this.exts = (this.dataset.exts || 'pdf,dwg,dxf,step,stp,iges,igs,png,jpg,jpeg,zip')
        .toLowerCase().split(',').map(function (e) { return e.trim(); });

      this.entries = []; // { file, state: 'uploading'|'done'|'error', fileId, url, row }

      this.trigger = this.querySelector('[data-upload-trigger]');
      this.input = this.querySelector('input[type="file"]');
      this.list = this.querySelector('[data-upload-list]');
      this.form = this.closest('form');
      this.urlsField = this.form ? this.form.querySelector('[data-upload-urls]') : null;
      this.submitBtn = this.form ? this.form.querySelector('button[type="submit"]') : null;

      if (!this.trigger || !this.input || !this.list || !this.form) return;

      var self = this;
      this.trigger.addEventListener('click', function () { self.input.click(); });
      this.input.addEventListener('change', function (e) {
        self.addFiles(e.target.files);
        e.target.value = '';
      });
      this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
    }

    addFiles(fileList) {
      var files = Array.prototype.slice.call(fileList || []);
      for (var i = 0; i < files.length; i++) {
        if (this.entries.length >= this.maxFiles) break;
        var file = files[i];
        var ext = (file.name.split('.').pop() || '').toLowerCase();
        var entry = { file: file, state: 'uploading', fileId: null, url: null, row: null };

        if (this.exts.indexOf(ext) === -1 || !file.size || file.size > this.maxBytes) {
          entry.state = 'error';
          entry.row = this.renderRow(entry);
          continue;
        }
        entry.row = this.renderRow(entry);
        this.entries.push(entry);
        this.upload(entry);
      }
      this.syncSubmitState();
    }

    upload(entry) {
      var self = this;
      var body = new FormData();
      body.append('file', entry.file, entry.file.name);

      fetch(this.endpoint + '?action=upload', { method: 'POST', body: body })
        .then(function (res) {
          if (!res.ok) throw new Error('upload http ' + res.status);
          return res.json();
        })
        .then(function (data) {
          entry.state = 'done';
          entry.fileId = data.fileId || null;
          entry.url = data.url || null;
          self.updateRow(entry);
          self.syncUrlsField();
          self.syncSubmitState();
        })
        .catch(function (err) {
          console.error('[contacto-upload]', err);
          entry.state = 'error';
          self.updateRow(entry);
          self.syncSubmitState();
        });
    }

    renderRow(entry) {
      var row = document.createElement('li');
      row.className = 'contacto__file';
      row.dataset.state = entry.state;

      var sizeMb = (entry.file.size / 1024 / 1024).toFixed(1);
      row.innerHTML =
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 2h8l4 4v16H6zM14 2v4h4"/></svg>' +
        '<span class="contacto__file-name"></span>' +
        '<span class="contacto__file-meta"></span>' +
        '<button type="button" class="contacto__file-remove"></button>';

      row.querySelector('.contacto__file-name').textContent = entry.file.name;

      var removeBtn = row.querySelector('.contacto__file-remove');
      removeBtn.setAttribute('aria-label', (this.dataset.labelRemove || 'Quitar') + ' ' + entry.file.name);
      removeBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M6 18 18 6"/></svg>';

      var self = this;
      removeBtn.addEventListener('click', function () { self.removeEntry(entry); });

      this.setRowMeta(row, entry, sizeMb);
      this.list.appendChild(row);
      this.list.hidden = false;
      return row;
    }

    setRowMeta(row, entry, sizeMb) {
      var meta = row.querySelector('.contacto__file-meta');
      if (entry.state === 'uploading') {
        meta.textContent = this.dataset.labelUploading || 'Subiendo…';
      } else if (entry.state === 'error') {
        meta.textContent = this.dataset.labelError || 'Error al subir';
      } else {
        meta.textContent = sizeMb + ' MB';
      }
    }

    updateRow(entry) {
      if (!entry.row) return;
      entry.row.dataset.state = entry.state;
      this.setRowMeta(entry.row, entry, (entry.file.size / 1024 / 1024).toFixed(1));
    }

    removeEntry(entry) {
      var idx = this.entries.indexOf(entry);
      if (idx !== -1) this.entries.splice(idx, 1);
      if (entry.row && entry.row.parentNode) entry.row.parentNode.removeChild(entry.row);
      if (!this.list.children.length) this.list.hidden = true;
      this.syncUrlsField();
      this.syncSubmitState();
    }

    pendingUploads() {
      return this.entries.some(function (e) { return e.state === 'uploading'; });
    }

    syncSubmitState() {
      if (!this.submitBtn) return;
      var pending = this.pendingUploads();
      this.submitBtn.disabled = pending;
      this.submitBtn.setAttribute('aria-busy', pending ? 'true' : 'false');
    }

    syncUrlsField() {
      if (!this.urlsField) return;
      var urls = this.entries
        .filter(function (e) { return e.state === 'done' && e.url; })
        .map(function (e) { return e.url; });
      this.urlsField.value = urls.join('\n');
    }

    fieldValue(name) {
      var el = this.form.querySelector('[name="' + name + '"]');
      return el ? el.value : '';
    }

    onSubmit(e) {
      if (this.submitted) return; // segunda pasada: dejar pasar el submit nativo
      if (this.pendingUploads()) { e.preventDefault(); return; }
      e.preventDefault();

      var self = this;
      var payload = {
        empresa: this.fieldValue('contact[Empresa]'),
        persona: this.fieldValue('contact[Persona de contacto]'),
        email: this.fieldValue('contact[email]'),
        referencia: this.fieldValue('contact[Referencia]'),
        familia: this.fieldValue('contact[Familia de producto]'),
        cantidad: this.fieldValue('contact[Cantidad estimada]'),
        plazo: this.fieldValue('contact[Plazo objetivo]'),
        detalle: this.fieldValue('contact[body]'),
        origen: this.fieldValue('contact[Origen]'),
        fileIds: this.entries
          .filter(function (en) { return en.state === 'done' && en.fileId; })
          .map(function (en) { return en.fileId; }),
      };

      // Registrar en el panel (metaobject) con timeout corto; pase lo que pase,
      // el formulario nativo se envía después (el email nunca se bloquea).
      var register = fetch(this.endpoint + '?action=submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(function (err) { console.error('[contacto-upload] submit', err); });

      var timeout = new Promise(function (resolve) { setTimeout(resolve, 6000); });

      Promise.race([register, timeout]).then(function () {
        self.submitted = true;
        if (typeof self.form.requestSubmit === 'function') {
          self.form.requestSubmit();
        } else {
          self.form.submit();
        }
      });
    }
  }

  customElements.define('surisa-contacto-upload', SurisaContactoUpload);
})();
